import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface CategoryEditModalProps {
  category: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  saving: boolean;
}

export function CategoryEditModal({
  category,
  isOpen,
  onClose,
  onSave,
  saving
}: CategoryEditModalProps) {
  const [formData, setFormData] = useState({
    display_name: '',
    description: '',
    linguistic_type: '',
    business_relevance: {
      importance: '',
      example_use_case: '',
      key_contexts: [] as string[]
    },
    cefr_coverage: {
      core_level: '',
      introduction_range: ['', ''] as string[],
      mastery_range: ['', ''] as string[]
    },
    teaching_approach: {
      recommended_method: '',
      key_concepts: [] as string[],
      practice_focus: [] as string[]
    },
    common_challenges: [] as Array<{ issue: string; description: string }>
  });

  useEffect(() => {
    if (category) {
      setFormData({
        display_name: category.display_name || '',
        description: category.description || '',
        linguistic_type: category.linguistic_type || '',
        business_relevance: {
          importance: category.business_relevance?.importance || '',
          example_use_case: category.business_relevance?.example_use_case || '',
          key_contexts: category.business_relevance?.key_contexts || []
        },
        cefr_coverage: {
          core_level: category.cefr_coverage?.core_level || '',
          introduction_range: category.cefr_coverage?.introduction_range || ['', ''],
          mastery_range: category.cefr_coverage?.mastery_range || ['', '']
        },
        teaching_approach: {
          recommended_method: category.teaching_approach?.recommended_method || '',
          key_concepts: category.teaching_approach?.key_concepts || [],
          practice_focus: category.teaching_approach?.practice_focus || []
        },
        common_challenges: category.common_challenges || []
      });
    }
  }, [category]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clean up empty values
    const cleanedData = {
      ...formData,
      cefr_coverage: {
        ...formData.cefr_coverage,
        introduction_range: formData.cefr_coverage.introduction_range.filter(Boolean),
        mastery_range: formData.cefr_coverage.mastery_range.filter(Boolean)
      }
    };
    
    onSave(cleanedData);
  };

  const handleAddChallenge = () => {
    setFormData(prev => ({
      ...prev,
      common_challenges: [...prev.common_challenges, { issue: '', description: '' }]
    }));
  };

  const handleRemoveChallenge = (index: number) => {
    setFormData(prev => ({
      ...prev,
      common_challenges: prev.common_challenges.filter((_, i) => i !== index)
    }));
  };

  const handleChallengeChange = (index: number, field: 'issue' | 'description', value: string) => {
    setFormData(prev => ({
      ...prev,
      common_challenges: prev.common_challenges.map((challenge, i) =>
        i === index ? { ...challenge, [field]: value } : challenge
      )
    }));
  };

  const cefr_levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Category: {category.display_name}</DialogTitle>
          <DialogDescription>
            Update the metadata for this grammar category
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="display_name">Display Name</Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="e.g., Modal Verbs"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detailed description of this grammar category..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="linguistic_type">Linguistic Type</Label>
              <Select
                value={formData.linguistic_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, linguistic_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="syntactic">Syntactic</SelectItem>
                  <SelectItem value="morphological">Morphological</SelectItem>
                  <SelectItem value="lexical">Lexical</SelectItem>
                  <SelectItem value="pragmatic">Pragmatic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* CEFR Coverage */}
          <div className="space-y-4">
            <h3 className="font-semibold">CEFR Coverage</h3>
            
            <div>
              <Label htmlFor="core_level">Core Level</Label>
              <Select
                value={formData.cefr_coverage.core_level}
                onValueChange={(value) => setFormData(prev => ({
                  ...prev,
                  cefr_coverage: { ...prev.cefr_coverage, core_level: value }
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select level..." />
                </SelectTrigger>
                <SelectContent>
                  {cefr_levels.map(level => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Business Relevance */}
          <div className="space-y-4">
            <h3 className="font-semibold">Business Relevance</h3>
            
            <div>
              <Label htmlFor="importance">Importance</Label>
              <Select
                value={formData.business_relevance.importance}
                onValueChange={(value) => setFormData(prev => ({
                  ...prev,
                  business_relevance: { ...prev.business_relevance, importance: value }
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select importance..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="essential">Essential</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="use_case">Example Use Case</Label>
              <Textarea
                id="use_case"
                value={formData.business_relevance.example_use_case}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  business_relevance: { ...prev.business_relevance, example_use_case: e.target.value }
                }))}
                placeholder="How is this used in business contexts?"
                rows={2}
              />
            </div>
          </div>

          {/* Common Challenges */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Common Challenges</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddChallenge}
              >
                Add Challenge
              </Button>
            </div>
            
            {formData.common_challenges.map((challenge, index) => (
              <div key={index} className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Challenge title"
                      value={challenge.issue}
                      onChange={(e) => handleChallengeChange(index, 'issue', e.target.value)}
                    />
                    <Textarea
                      placeholder="Challenge description"
                      value={challenge.description}
                      onChange={(e) => handleChallengeChange(index, 'description', e.target.value)}
                      rows={2}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveChallenge(index)}
                    className="ml-2"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}